/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: FaxViewerPage.tsx
 ??? Created: 2025-12-03
 ?? Description:
    Main page for viewing fax logs and downloading fax files
----------------------------------------------------------------*/

import { useState, useCallback, useMemo } from 'react';
import { Eye, Download, Trash2, FileText, CheckCircle, XCircle, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useFaxLogs } from './hooks/useFaxLogs';
import { deleteFaxLog, downloadFaxFile } from './api/faxViewerApi';
import FaxViewerModal from './FaxViewerModal';
import type { FaxLog } from './types';

export default function FaxViewerPage() {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; log: FaxLog | null; deleteFiles: boolean }>({
    isOpen: false,
    log: null,
    deleteFiles: false
  });

  // PDF/Image viewer modal
  const [viewerModal, setViewerModal] = useState<{ isOpen: boolean; blobUrl: string | null; title: string }>({
    isOpen: false,
    blobUrl: null,
    title: ''
  });

  // Revoke blob URL on modal close
  const closeViewer = useCallback(() => {
    if (viewerModal.blobUrl) URL.revokeObjectURL(viewerModal.blobUrl);
    setViewerModal({ isOpen: false, blobUrl: null, title: '' });
  }, [viewerModal.blobUrl]);


  // Define columns
  const availableColumns = useMemo(() => [
    {
      key: 'createdAt',
      label: t('faxViewer:columns.date'),
      sortable: true,
      visible: true,
      render: (_value: unknown, log: FaxLog) => (
        <span className="text-sm text-foreground">
          {new Date(log.createdAt).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'direction',
      label: t('faxViewer:columns.direction'),
      sortable: true,
      visible: true,
      render: (_value: unknown, log: FaxLog) => {
        const isInbound = log.direction === 'inbound';
        return (
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            isInbound
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
              : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
          }`}>
            {isInbound ? <ArrowDownCircle className="h-3 w-3" /> : <ArrowUpCircle className="h-3 w-3" />}
            {t(`faxViewer:directions.${log.direction}`)}
          </span>
        );
      }
    },
    {
      key: 'faxDeviceName',
      label: t('faxViewer:columns.device'),
      sortable: false,
      visible: true,
      render: (_value: unknown, log: FaxLog) => (
        <div>
          <div className="font-medium text-foreground">{log.faxDeviceName}</div>
          <div className="text-xs text-muted-foreground font-mono">{log.faxDeviceNumber}</div>
        </div>
      ),
    },
    {
      key: 'remoteNumber',
      label: t('faxViewer:columns.remote'),
      sortable: true,
      visible: true,
      render: (_value: unknown, log: FaxLog) => (
        <span className="font-mono text-sm text-foreground">{log.remoteNumber || '-'}</span>
      ),
    },
    {
      key: 'pages',
      label: t('faxViewer:columns.pages'),
      sortable: true,
      visible: true,
      render: (_value: unknown, log: FaxLog) => (
        <span className="text-sm text-center">{log.pages || 0}</span>
      ),
    },
    {
      key: 'success',
      label: t('faxViewer:columns.status'),
      sortable: true,
      visible: true,
      render: (_value: unknown, log: FaxLog) => (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
          log.success
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
        }`}>
          {log.success ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
          {log.success ? t('faxViewer:status.success') : t('faxViewer:status.failed')}
        </span>
      )
    },
    {
      key: 'fileSize',
      label: t('faxViewer:columns.size'),
      sortable: true,
      visible: false,
      render: (_value: unknown, log: FaxLog) => {
        if (!log.fileSize) return '-';
        const kb = (log.fileSize / 1024).toFixed(2);
        return <span className="text-sm text-muted-foreground">{kb} KB</span>;
      }
    },
    {
      key: 'actions',
      label: t('common:actions'),
      sortable: false,
      visible: true,
    },
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useFaxLogs();

  // Override columns with our custom renders
  // If no saved column preferences exist (columns=[]), fall back to availableColumns
  const finalColumns = useMemo(() => {
    if (columns.length === 0) {
      return availableColumns; // Use default columns when no preferences saved
    }
    return columns.map((col) => {
      const customCol = availableColumns.find(c => c.key === col.key);
      if (customCol && customCol.render) {
        return { ...col, render: customCol.render };
      }
      return col;
    });
  }, [columns, availableColumns]);

  // Handlers
  const handleView = useCallback(async (log: FaxLog) => {
    try {
      const blobUrl = await downloadFaxFile(log.id, 'pdf', true);
      if (blobUrl) {
        const label = `Fax ${log.direction === 'inbound' ? 'Received' : 'Sent'} � ${new Date(log.createdAt).toLocaleString()}`;
        setViewerModal({ isOpen: true, blobUrl, title: label });
      }
    } catch (error) {
      showError(error.message || t('faxViewer:messages.downloadError'));
    }
  }, [showError, t]);

  const handleDownload = useCallback(async (log: FaxLog) => {
    try {
      // Always download as PDF (backend converts TIFF?PDF via tiff2pdf if needed)
      await downloadFaxFile(log.id, 'pdf', false);
    } catch (error) {
      showError(error.message || t('faxViewer:messages.downloadError'));
    }
  }, [showError, t]);

  const handleDelete = useCallback((log: FaxLog) => {
    setDeleteModal({ isOpen: true, log, deleteFiles: false });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteModal.log) return;

    try {
      await deleteFaxLog(deleteModal.log.id, deleteModal.deleteFiles);
      showSuccess(t('faxViewer:messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, log: null, deleteFiles: false });
      refresh();
    } catch (error) {
      showError(error.message || t('faxViewer:messages.deleteError'));
    }
  }, [deleteModal.log, deleteModal.deleteFiles, showSuccess, showError, t, refresh]);

  // Render row actions
  const renderActions = useCallback((log: FaxLog) => (
    <div className="flex gap-1">
      {log.success && (log.pdfPath || log.filePath) && (
        <>
          <SimpleTooltip content={t('faxViewer:actions.view')} position="top">
            <button
              onClick={() => handleView(log)}
              className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
            >
              <Eye className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </button>
          </SimpleTooltip>

          <SimpleTooltip content={t('faxViewer:actions.download')} position="top">
            <button
              onClick={() => handleDownload(log)}
              className="p-2 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
            >
              <Download className="h-4 w-4 text-green-600 dark:text-green-400" />
            </button>
          </SimpleTooltip>
        </>
      )}

      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDelete(log)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [t, handleView, handleDownload, handleDelete]);

  return (
    <div className="p-6">
      <DataGridSimple
        data={data}
        loading={loading}
        columns={finalColumns}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={handleSearch}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onColumnToggle={handleColumnToggle}
        renderActions={renderActions}
        searchPlaceholder={t('faxViewer:searchPlaceholder')}
        title={t('faxViewer:title')}
        titleIcon={<FileText className="h-5 w-5 text-primary" />}
      />

      {/* PDF Viewer Modal � rendered via createPortal into document.body */}
      {viewerModal.isOpen && viewerModal.blobUrl && (
        <FaxViewerModal
          blobUrl={viewerModal.blobUrl}
          title={viewerModal.title}
          onClose={closeViewer}
        />
      )}

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, log: null, deleteFiles: false })}
        onConfirm={confirmDelete}
        title={t('faxViewer:messages.deleteTitle')}
        message={t('faxViewer:messages.confirmDelete')}
        confirmText={t('faxViewer:actions.delete')}
        cancelText={t('common:cancel')}
        type="danger"
      />
    </div>
  );
}

